import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ChangeDetectionStrategy
} from "@angular/core";
import { Subscription } from "rxjs";

import { Device } from "../models/device";
import { BackEndService } from "../services/http/back-end.service";
import { ProvisionerService } from "../services/engine/provisioner.service";
import { FullscreenLoaderComponent } from "../fullscreen-loader/fullscreen-loader.component";
import { FormsModule } from "@angular/forms";
import { MatButton, MatIconButton } from "@angular/material/button";

import { QrScannerComponent } from "../qr-scanner-component/qr-scanner.component";
import { MatIcon } from "@angular/material/icon";
import { MatInput } from "@angular/material/input";

@Component({
  selector: "app-device-flasher",
  templateUrl: "./device-flasher.component.html",
  styleUrls: ["./device-flasher.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FullscreenLoaderComponent,
    FormsModule,
    MatButton,
    QrScannerComponent,
    MatIconButton,
    MatIcon,
    MatInput
  ]
})
export class DeviceFlasherComponent implements OnDestroy {
  private subscription: Subscription = new Subscription();

  static validationPending = false;

  private readonly SIGFOX_REGEX: RegExp = /[0123456789ABCDEF]{7}/;
  private readonly SENSOLUS_REGEX: RegExp = /^(?:SNT_|N5_)?[A-Z0-9]{6}$/i;
  private readonly CAMERA_HIDDEN = { width: "0", height: "0" };
  private readonly CAMERA_VISIBLE = { width: "95vw", height: "auto" };
  private readonly FLASH_PATH = "../../assets/flash.svg";

  public selectedDevice: MediaDeviceInfo = null;
  public inputDeviceId: string;
  public lastResultString: string;

  @Input() nbFlashedDevices = 0;
  @Input() flasherType = "";
  @Output() newDevice: EventEmitter<Device> = new EventEmitter();

  public error: string[] = [];
  public success: string[] = [];
  public timer: number = null;
  public cameraError = "";
  public cameraStyle = this.CAMERA_HIDDEN;
  public qrCodeWidth = 0;
  public cameraEnabled = false;
  public permissionAsked = true;
  public selectedCamera: string;
  public listCameras: any = [];
  public isFlashOn = false;
  public isFlashAllowed = false;

  private deviceQueue: Device[] = [];
  private deviceAddedInList: Device[] = [];
  private isProcessingQueue = false;

  constructor(
    private provisioning: ProvisionerService,
    private backend: BackEndService
  ) {
    this.subscription.add(
      this.provisioning.validateDevice.subscribe((deviceToValidate: Device) => {
        if (this.timer !== null) {
          clearTimeout(this.timer);
        }

        if (deviceToValidate !== undefined && !deviceToValidate.isValid) {
          this.setError(
            deviceToValidate.errorMessage
              ? deviceToValidate.errorMessage
              : `${deviceToValidate.deviceId}: Invalid ID.`
          );
        } else if (deviceToValidate === undefined) {
          this.setError(
            `${deviceToValidate.deviceId}: Unable to reach the server. Please check your internet connection.`
          );
        } else {
          this.deviceAddedInList.push(deviceToValidate);
          this.setSuccess(
            this.flasherType === "inventory"
              ? `Device ${deviceToValidate.deviceId} found`
              : `Device ${deviceToValidate.deviceId} added in the list`
          );
        }
        this.finishDeviceProcessing();
      })
    );

    this.subscription.add(
      this.provisioning.deviceListEvent.subscribe((list: Device[]) => {
        if (
          list !== undefined &&
          (list.length !== 0 || this.nbFlashedDevices === 0)
        ) {
          this.nbFlashedDevices = list.length;
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();

    DeviceFlasherComponent.validationPending = false;
    if (this.timer !== null) {
      clearTimeout(this.timer);
    }
  }

  public parseQRResult(qrValue: string): void {
    console.log("QRCode scanned - ", qrValue);

    this.resetMsgs();
    try {
      if (!qrValue) {
        throw new Error("QR code empty or invalid");
      }

      const value = qrValue.trim();

      // Case 1: SIGFOX tracker
      // Exemple id : "104649F", Example QRCode: "ALPS ELECTRIC ID 0104649F C5_REL"
      if (value.includes("ID")) {
        const parts = value.split(/\s+/);
        const idIndex = parts.indexOf("ID") + 1;

        if (idIndex <= 0 || idIndex >= parts.length) {
          console.log(`Invalid format for SIGFOX tracker: ${value}`);
          throw new Error("Invalid format for SIGFOX tracker");
        }

        let deviceId = parts[idIndex];

        // ✅ Suppression d’un zéro inutile au début
        // Si l’ID commence par "0" suivi d’un chiffre, on le retire
        if (/^0[0-9A-F]+$/i.test(deviceId)) {
          deviceId = deviceId.substring(1);
        }

        console.log("[S F X]", deviceId);
        this.addDevice(deviceId.toUpperCase(), "SFX");
        return;
      }

      // Case 1: N5 tracker
      // Exemple id : "TCXJ3V", Example QRCode: "TCXJ3V,89882390000580542408" OR "*TCXJ3V,23626492E209104*"
      if (value.includes(",") && value.split(",").length === 2) {
        const cleanedValue = value.replace(/^\*|\*$/g, "").trim();
        const firstPart = cleanedValue.split(",")[0].trim().toUpperCase();

        // Validation stricte : exactement 6 caractères alphanumériques
        if (!/^[A-Z0-9]{6}$/.test(firstPart)) {
          console.log(`Invalid format for N5 tracker: ${value}`);
          throw new Error("Invalid format for N5 tracker");
        }

        console.log("(N 5)", firstPart);
        this.addDevice(firstPart.toUpperCase(), "N5");
        return;
      }

      // Case 3: SNT tracker
      // Exemple id : "94XUAC", Example QRCode: "94XUAC"
      if (/^[A-Z0-9]{5,6}$/i.test(value)) {
        console.log("{S N T}", value);
        this.addDevice(value.toUpperCase(), "SNT");
        return;
      }

      throw new Error("QR code invalid");
    } catch (err) {
      this.setError(err);
      this.setTimer(5000);
    }
  }

  public queueMax = 0;
  public queueCurrent = 1;
  public currentDeviceId = "";
  public activeLoader = false;

  public handleInput(): void {
    if (!this.inputDeviceId) {
      return;
    }

    let ids = this.inputDeviceId
      .trim()
      .toUpperCase()
      .split(/\s+/)
      .filter(Boolean); // Delete all empty values (security)

    this.inputDeviceId = "";
    this.resetMsgs();

    if (ids.length > 1) {
      if (this.flasherType === "inventory") {
        ids = [ids[0]]; // keep only the first deviceId if we are on the inventory tab
      } else {
        this.queueCurrent = 0;
        this.queueMax = ids.length;
        setTimeout(() => {
          // force le trigger du DOM, aucun autre moyen trouvé pour le moment
          this.activeLoader = true;
        });
      }
    }

    ids.forEach((id) => {
      try {
        this.currentDeviceId = id;
        this.parsedInputId(id);
      } catch (err) {
        console.error(err);
        this.setError(`${id}: Invalid ID format.`);
      }
    });
  }

  public parsedInputId(deviceId: string): void {
    // Case 1: SIGFOX ID
    // Exemple id : "104649F", "0104649F", "000000104649F", "SFX_104649F", "SFX_0104649F"..
    if (deviceId.match(this.SIGFOX_REGEX)) {
      console.log("SFX device match");

      const match = deviceId
        .replace(/^SFX_/, "")
        .replace(/^0+/, "")
        .match(/[0-9A-F]{7}$/i);

      if (!match) {
        throw new Error(`Entrée invalide : "${deviceId}"`);
      }

      this.addDevice(match[0], "SFX");
    }

    // Case 2: SENSOLUS ID
    // Exemple id : "TCXJ3V", "N5_TCXJ3V", "94XUAC", "SNT_94XUAC"..
    else if (deviceId.match(this.SENSOLUS_REGEX)) {
      console.log("Hati device match");

      if (deviceId.startsWith("SNT_")) {
        this.addDevice(deviceId.replace(/^SNT_/, ""), "SNT");
      } else if (deviceId.startsWith("N5_")) {
        this.addDevice(deviceId.replace(/^N5_/, ""), "N5");
      } else {
        console.log("Fetch Sensolus type from back-end");

        this.subscription.add(
          this.backend.getSensolusType(deviceId).subscribe({
            next: (data) => {
              console.log("Sensolus type data", data);
              if (!data?.type || !data?.id) {
                this.setError(`${deviceId}: Invalid Sensolus type response`);
                throw new Error("Invalid Sensolus type response");
              }

              this.addDevice(data.id, data.type);
            },
            error: (err) => {
              console.error("Error fetching Sensolus type", err);
              this.setError(
                `${deviceId}: Unable to determine device type. Please check the ID or try again later.`
              );
            }
          })
        );
      }
    } else {
      console.log("No device match");
      this.setError(
        `${deviceId}: Invalid ID format. Please check the ID and try again.`
      );
    }
  }

  private addDevice(deviceId: string, deviceType: string): void {
    const device: Device = Device.getEmpty();

    device.deviceId = `${deviceType}_${deviceId}`;
    device.deviceType = deviceType;

    this.deviceQueue.push(device);
    this.processQueue();
  }

  private processQueue(): void {
    if (this.isProcessingQueue || this.deviceQueue.length === 0) {
      if (this.deviceQueue.length === 0) {
        if (this.error.length) {
          // if an error was detected in the device batch, delete it
          for (const device of this.deviceAddedInList) {
            this.provisioning.deleteDevice(device);
          }
        }

        this.deviceAddedInList = [];
        this.setTimer(this.error.length ? this.error.length * 5000 : 2000);
        setTimeout(() => {
          this.activeLoader = false;
        });
        this.provisioning.enableActions.emit(true);
      }
      return;
    }

    this.isProcessingQueue = true;

    const device = this.deviceQueue.shift();

    this.queueCurrent++;
    this.currentDeviceId = device.deviceId;

    DeviceFlasherComponent.validationPending = true;

    this.newDevice.emit(device);
  }

  private finishDeviceProcessing(): void {
    DeviceFlasherComponent.validationPending = false;
    this.isProcessingQueue = false;
    this.processQueue(); // device suivant
  }

  private setError(errorMsg: string) {
    this.error.push(errorMsg);
  }

  private setSuccess(successMsg: string) {
    this.success.push(successMsg);
  }

  private setTimer(time: number) {
    if (this.timer !== null) {
      clearTimeout(this.timer);
    }

    this.timer = window.setTimeout(() => {
      this.success = [];
      this.error = [];
      if (this.cameraEnabled) this.inputDeviceId = "";
    }, time);
  }

  public resetMsgs() {
    if (this.timer !== null) {
      clearTimeout(this.timer);
    }

    this.error = [];
    this.success = [];
  }

  public close() {
    this.cameraEnabled = false;
    this.cameraStyle = this.CAMERA_HIDDEN;
    this.qrCodeWidth = 0;
  }

  public showCamera() {
    this.cameraEnabled = true;
    this.cameraStyle = this.CAMERA_VISIBLE;
    this.qrCodeWidth = 50;
  }

  public reloadPermissions() {
    this.provisioning.reloadDeviceFlash.emit();
  }

  public setCamerasList(cameras): void {
    this.listCameras = cameras;
  }

  public onCameraChange(cameraId: string): void {
    this.selectedCamera = cameraId;
    localStorage.setItem("preferredCameraId", cameraId);
  }

  public onFlashCompatible(event): void {
    this.isFlashAllowed = event;
  }

  public toggleFlash(): void {
    this.isFlashOn = !this.isFlashOn;
  }
}
